// components
import { MenuItem, MenuList, Typography } from '@mui/material';
import { Popover } from 'components/mrp/components/Popover';

// hooks
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/shallow';
import { useCalendarInteractionStore } from '../../../store/calendarInteractionStore.store';

// styles
import {
  DisconnectIcon,
  StyledEntityQuickDisconnect,
  StyledMenuItem
} from './EntityQuickDisconnect.styles';

// types
import { EntityUnion } from './EntityQuickDisconnect.types';
import { showErrorSnackbar } from 'components/shared/Snackbar';
import {
  extractPlanningEntityIdentifiersFromPlanningEvent,
  getDeselectedClients,
  getDeselectedEmployees,
  getDeselectedResources,
  getDeselectedWorkPlace,
  getEntityLabel
} from './EntityQuickDisconnect.utils';
import { EventResponse } from 'generated/index';

// utils
import { CalendarInteractionContext } from '../../../CalendarInteractionProvider/CalendarInteractionProvider.context';
import { createAppointmentFormDataFromEventResponse } from 'components/mrp/components/Calendar/components/CalendarAppointment/CalenderAppointment.utils';
import { getEntityIcon } from 'components/mrp/mrp.utils';

export const EntityQuickDisconnect = () => {
  const { t } = useTranslation('common', { keyPrefix: 'mrp' });
  const {
    eventRef,
    showEntityQuickDisconnect,
    setShowEntityQuickDisconnect,
    sourceEvent,
    setSourceEvent
  } = useCalendarInteractionStore(
    useShallow((state) => ({
      eventRef: state.eventRef,
      showEntityQuickDisconnect: state.showEntityQuickDisconnect,
      setShowEntityQuickDisconnect: state.control.setShowEntityQuickDisconnect,
      sourceEvent: state.sourceEvent,
      setSourceEvent: state.control.setSourceEvent
    }))
  );

  const { updateAppointment } = useContext(CalendarInteractionContext);

  const { planningEvent } = sourceEvent || {};

  const entities = [
    ...(planningEvent?.clients || []).map((c) => ({
      ...c,
      planningEntityType: 'CLIENT'
    })),
    ...(planningEvent?.employees || []).map((e) => ({
      ...e,
      planningEntityType: 'EMPLOYEE'
    })),
    ...(planningEvent?.resources || []).map((r) => ({
      ...r,
      planningEntityType: 'RESOURCE'
    })),
    planningEvent?.workPlace && {
      ...planningEvent.workPlace,
      planningEntityType: 'WORKPLACE'
    }
  ].filter(Boolean) as EntityUnion[];

  const handleDisconnectEntity = async (entity: EntityUnion) => {
    if (!planningEvent || !planningEvent.id) return;

    const {
      clients = [],
      employees = [],
      resources = [],
      workPlace,
      ...restPlanningEvent
    } = planningEvent;

    const oldEntities =
      extractPlanningEntityIdentifiersFromPlanningEvent(planningEvent);

    try {
      const updatedAppointmentEntities: Partial<EventResponse> = {
        ...restPlanningEvent,
        ...(getDeselectedClients(clients, entity).length > 0 && {
          clients: getDeselectedClients(clients, entity)
        }),
        ...(getDeselectedEmployees(employees, entity).length > 0 && {
          employees: getDeselectedEmployees(employees, entity)
        }),
        ...(getDeselectedResources(resources, entity).length > 0 && {
          resources: getDeselectedResources(resources, entity)
        }),
        ...(getDeselectedWorkPlace(
          workPlace,
          entity,
          planningEvent.workPlace?.entityId
        ) && {
          workPlaceEntityId: getDeselectedWorkPlace(
            workPlace,
            entity,
            planningEvent.workPlace?.entityId
          )
        })
      };

      updateAppointment(
        createAppointmentFormDataFromEventResponse(updatedAppointmentEntities),
        oldEntities
      );
    } catch {
      showErrorSnackbar();
    }

    handleClose();
  };

  const handleClose = () => {
    setSourceEvent(undefined, false);
    setShowEntityQuickDisconnect(false);
  };

  return (
    <Popover
      childRef={eventRef}
      useForcedState
      forceOpen={showEntityQuickDisconnect}
      onClose={handleClose}
      id="entity-quick-disconnect-popover"
      anchorOrigin={{
        horizontal: 'left',
        vertical: 'bottom'
      }}
      component={
        <StyledEntityQuickDisconnect>
          <MenuList dense>
            <MenuItem disabled>
              <Typography variant="body1">{t('quickDisconnect')}</Typography>
            </MenuItem>
            {entities.map((entity) => {
              const label = getEntityLabel(entity);

              return (
                <StyledMenuItem
                  key={entity?.id || entity?.entityId}
                  autoFocus
                  onClick={handleDisconnectEntity.bind(null, entity)}
                >
                  {getEntityIcon(entity.planningEntityType, label)}
                  <DisconnectIcon type="link-slash" />
                </StyledMenuItem>
              );
            })}
          </MenuList>
        </StyledEntityQuickDisconnect>
      }
    />
  );
};

export default EntityQuickDisconnect;
